/**
 * Evidence rule for the recovery-time camera demotion.
 *
 * `selectRecoveryQualityTier` steps a returning guest down one tier on the theory that "a
 * link that could not hold its resolution has not earned it back". That theory assumes the
 * reconnect was caused by link CAPACITY. It often is not: a guest refreshes its connection
 * because a receive-side jitter buffer stood up on a clean link, because a stall-escalation
 * timer fired, or because the player reloaded. The 2026-09-20 Android session is the shape:
 * 0.0% loss, 4-9 ms RTT, no bandwidth limitation, and a 285 ms jitter-buffer target; the
 * reconnect was demoted 480p -> 360p, and the target ramped to 212 ms at 360p just the same.
 * A smaller picture does not cure a receiver-side buffer, and it costs the guest a tier for
 * the rest of the session.
 *
 * So the demotion now needs evidence the LINK was in trouble in the window before the drop.
 * Anything that is not capacity - a standing buffer above all - keeps the camera in place.
 * Browser-free and pure: the host feeds it the samples it already polls.
 */

import { classifyLinkImpairment } from './linkImpairment';

/** One host-side stats poll, as the demotion rule needs to see it. */
export type LinkTroubleSample = {
  atMs: number;
  /** Outbound loss this poll, percent. */
  lossPct: number;
  /** Candidate-pair RTT this poll, ms; absent when the pair was not sampled. */
  rttMs?: number;
  /** outbound-rtp qualityLimitationReason, when reported ('none' | 'bandwidth' | 'cpu' | 'other'). */
  qualityLimitationReason?: string | null;
  /** True while the peer's congestion recovery stood at its critical floor (a loss/RTT-driven hard cut). */
  congestionCritical?: boolean;
  /** True while the host's standing-latency detector held a `standing-buffer` verdict for the peer. */
  standingBuffer?: boolean;
  /**
   * The calibrated glass-to-glass floor's excess over the link's own RTT floor, ms, from the
   * host's standing-latency detector (`excessMs`). Present only when that detector could NOT
   * confirm a receive-side standing buffer (verdict kind !== 'standing-buffer'). A large value
   * is a capacity signal only for engines that report no jitter-buffer counters (Safari). On
   * Chromium the same excess is the receiver: wireless loss or a playout-trend spike blocks
   * the standing-buffer verdict, and the round trip stays flat.
   */
  latencyFloorExcessMs?: number;
  /**
   * True when this poll's guest had already reported an inbound-rtp jitter-buffer delay.
   * Those guests can name a receive buffer; a glass-to-glass spike is not a route failure.
   */
  jitterBufferReported?: boolean;
};

export const RECOVERY_DEMOTION_EVIDENCE_WINDOW_MS = 30_000;
/** Loss in any one poll at or above this is capacity trouble. */
export const RECOVERY_DEMOTION_LOSS_PCT = 2;
/** RTT rising this far above the window's minimum is capacity trouble (queueing on the path). */
export const RECOVERY_DEMOTION_RTT_SPIKE_MS = 100;
/**
 * A calibrated g2g floor this far above the link's own RTT floor is latency the route cannot
 * account for. Only meaningful when the standing-latency detector could NOT confirm a receive
 * buffer (Safari exposes no jitter-buffer counters), so it is the capacity signal such guests
 * can still produce. Aligned with the standing-detector's own minExcessMs.
 */
export const RECOVERY_DEMOTION_LATENCY_FLOOR_EXCESS_MS = 75;
/** That excess must appear in at least this many polls before it authorizes a demotion. */
export const RECOVERY_DEMOTION_LATENCY_FLOOR_MIN_POLLS = 5;

export type RecoveryDemotionReason =
  /** Loss at or above RECOVERY_DEMOTION_LOSS_PCT in the window. */
  | 'loss'
  /** The encoder reported it was bandwidth-limited. */
  | 'bandwidth-limited'
  /** Recovery reached its critical floor: the link, not the receiver, forced a hard cut. */
  | 'congestion-critical'
  /** RTT rose RECOVERY_DEMOTION_RTT_SPIKE_MS above its floor: queueing on the path. */
  | 'rtt-spike'
  /**
   * The g2g floor sat far above the RTT floor for a sustained stretch while the standing
   * detector could not confirm a receive buffer — the capacity signal no-counter engines
   * (Safari) can still produce. A receiver buffer is already drained directly; this is the link.
   */
  | 'latency-floor-over-route'
  /** No polls on record for this peer: the connection never carried stats, so the old rule stands. */
  | 'no-samples'
  /** The link was clean; the only finding was a receive-side standing buffer, which a tier change does not cure. */
  | 'standing-buffer-not-capacity'
  /** The link was clean and nothing stood out. */
  | 'no-capacity-evidence';

export type RecoveryDemotionDecision = {
  demote: boolean;
  reason: RecoveryDemotionReason;
  evidence: {
    samples: number;
    maxLossPct: number;
    rttMinMs: number | null;
    rttMaxMs: number | null;
    bandwidthLimitedPolls: number;
    criticalPolls: number;
    standingBufferPolls: number;
    /** Polls with latencyFloorExcessMs at/over threshold AND no confirmed standing buffer. */
    latencyFloorOverRoutePolls: number;
    /** Polls whose guest had reported an inbound-rtp jitter-buffer delay. */
    jitterBufferReportedPolls: number;
    /** Largest latencyFloorExcessMs seen in the window, when any. */
    maxLatencyFloorExcessMs: number | null;
  };
};

/**
 * Whether a returning guest should come back one tier lower.
 *
 * Demote on capacity evidence in the window: loss while the round trip climbs, a
 * bandwidth-limited encoder, a critical recovery floor, or an RTT spike. Flat-round-trip
 * loss is the radio and does not move the camera. A peer with no samples at all demotes - the connection
 * never carried stats, which is the connection failure the rule was written for. Everything
 * else keeps the camera, and a standing buffer is named as the reason so the log says why.
 */
export function decideRecoveryDemotion(samples: ReadonlyArray<LinkTroubleSample>, nowMs: number): RecoveryDemotionDecision {
  const since = nowMs - RECOVERY_DEMOTION_EVIDENCE_WINDOW_MS;
  const recent = samples.filter((s) => s.atMs >= since && s.atMs <= nowMs);
  const evidence = summarize(recent);
  if (recent.length === 0) return { demote: true, reason: 'no-samples', evidence };
  if (capacityLoss(recent)) return { demote: true, reason: 'loss', evidence };
  if (evidence.bandwidthLimitedPolls > 0) return { demote: true, reason: 'bandwidth-limited', evidence };
  if (evidence.criticalPolls > 0) return { demote: true, reason: 'congestion-critical', evidence };
  if (evidence.rttMinMs !== null && evidence.rttMaxMs !== null && evidence.rttMaxMs - evidence.rttMinMs >= RECOVERY_DEMOTION_RTT_SPIKE_MS) {
    return { demote: true, reason: 'rtt-spike', evidence };
  }
  // Capacity signal for engines that expose no jitter-buffer counters (Safari): the calibrated
  // g2g floor sits far above the link's own RTT floor, sustained, and the standing detector could
  // NOT confirm a receive buffer. A guest that reports jitter-buffer delay is excluded. On
  // Chromium, wireless loss above 0.5% or a playout-trend spike classifies the same excess as
  // `route`, so the standing-buffer guard never sees it — the 2026-10-07 Android demotion was
  // 9 such polls and a 287 ms excess while RTT stayed 4–9 ms.
  const guestReportsJitterBuffer = evidence.jitterBufferReportedPolls > 0;
  if (!guestReportsJitterBuffer && evidence.latencyFloorOverRoutePolls >= RECOVERY_DEMOTION_LATENCY_FLOOR_MIN_POLLS) {
    return { demote: true, reason: 'latency-floor-over-route', evidence };
  }
  return { demote: false, reason: evidence.standingBufferPolls > 0 ? 'standing-buffer-not-capacity' : 'no-capacity-evidence', evidence };
}

function summarize(samples: ReadonlyArray<LinkTroubleSample>): RecoveryDemotionDecision['evidence'] {
  let maxLossPct = 0;
  let rttMinMs: number | null = null;
  let rttMaxMs: number | null = null;
  let bandwidthLimitedPolls = 0;
  let criticalPolls = 0;
  let standingBufferPolls = 0;
  let latencyFloorOverRoutePolls = 0;
  let jitterBufferReportedPolls = 0;
  let maxLatencyFloorExcessMs: number | null = null;
  for (const s of samples) {
    if (Number.isFinite(s.lossPct)) maxLossPct = Math.max(maxLossPct, s.lossPct);
    if (typeof s.rttMs === 'number' && Number.isFinite(s.rttMs)) {
      rttMinMs = rttMinMs === null ? s.rttMs : Math.min(rttMinMs, s.rttMs);
      rttMaxMs = rttMaxMs === null ? s.rttMs : Math.max(rttMaxMs, s.rttMs);
    }
    if (s.qualityLimitationReason === 'bandwidth') bandwidthLimitedPolls += 1;
    if (s.congestionCritical) criticalPolls += 1;
    if (s.standingBuffer) standingBufferPolls += 1;
    if (s.jitterBufferReported) jitterBufferReportedPolls += 1;
    // The excess only counts as link capacity when no receive buffer was confirmed alongside it.
    if (
      s.standingBuffer !== true &&
      typeof s.latencyFloorExcessMs === 'number' &&
      Number.isFinite(s.latencyFloorExcessMs)
    ) {
      maxLatencyFloorExcessMs =
        maxLatencyFloorExcessMs === null ? s.latencyFloorExcessMs : Math.max(maxLatencyFloorExcessMs, s.latencyFloorExcessMs);
      if (s.latencyFloorExcessMs >= RECOVERY_DEMOTION_LATENCY_FLOOR_EXCESS_MS) latencyFloorOverRoutePolls += 1;
    }
  }
  return {
    samples: samples.length,
    maxLossPct,
    rttMinMs,
    rttMaxMs,
    bandwidthLimitedPolls,
    criticalPolls,
    standingBufferPolls,
    latencyFloorOverRoutePolls,
    jitterBufferReportedPolls,
    maxLatencyFloorExcessMs,
  };
}

/**
 * Loss demotes only when it is congestion. Flat-round-trip loss is the radio, and a
 * smaller camera does not put those packets back. A sample with no round trip still
 * counts: an unmeasured floor is not evidence the loss was wireless.
 */
function capacityLoss(samples: ReadonlyArray<LinkTroubleSample>): boolean {
  const floor = samples.reduce<number | null>((min, sample) => {
    if (typeof sample.rttMs !== 'number' || !Number.isFinite(sample.rttMs)) return min;
    return min === null ? sample.rttMs : Math.min(min, sample.rttMs);
  }, null);
  return samples.some((sample) => {
    if (sample.lossPct < RECOVERY_DEMOTION_LOSS_PCT) return false;
    const queue = floor === null || typeof sample.rttMs !== 'number' ? null : sample.rttMs - floor;
    return classifyLinkImpairment({ packetsLostPct: sample.lossPct, standingQueueMs: queue }) === 'congestion';
  });
}
